import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IProductPacking, ProductCard } from '../../../product/models/product-card';
import {
	IProductCard,
	IProductCardForm,
	IProductCardPackingForm,
	IProductCardParams,
	IProductGeneralInfo,
	IShowProductCard,
	ProductCardTab,
	ProductChangeTypes,
	ProductTypes
} from '../../../product/models/models';
import { forkJoin, from, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { FormArray, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from '../../services/snack-bar.service';
import { catchError, concatMap, switchMap } from 'rxjs/operators';
import { tinykeys } from 'tinykeys';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { ProductsService } from '../../services/products.service';
import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { HttpErrorResponse } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { GlobalSettings } from '../../models/globalSettings';
import { UnitConverter } from '../../utils/unitConverter';
import { DefaultProductParams } from '../../models/defaultProductParams';
import { ProductCardService } from '../../services/product-card.service';
import { MasterProductsTablesComponent } from '../master-products-tables/master-products-tables.component';
import { ProductUsageTableComponent } from '../product-usage-table/product-usage-table.component';
import { ProductChangeHistoryTableComponent } from '../product-change-history-table/product-change-history-table.component';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { MixpanelService } from '../../services/mixpanel.service';
import { DwUsersActivityService } from '../../services/dw-users-activity.service';
import { DwUserActivityNames } from '../../models/dwUserActivityNames';
import { DwUserActivityTypes } from '../../models/dwUsersActivityTypes';

import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { ProductCompressionParamsComponent } from '../product-compression-params/product-compression-params.component';
import { ProductGeneralInfoComponent } from '../product-general-info/product-general-info.component';
import { ProductMasterGeneralInfoComponent } from '../product-master-general-info/product-master-general-info.component';
import { ProductPackingEditComponent } from '../product-packing-edit/product-packing-edit.component';
import { ProductPhysParamsEditComponent } from '../product-phys-params-edit/product-phys-params-edit.component';

type Loader = {
	params: boolean;
	masterProducts: boolean;
	layout: boolean;
	history: boolean;
}

const INPUT_CONTROL_NAME: Array<string> = [
	'height',
	'width',
	'depth',
	'width_compression_coef',
	'height_compression_coef',
	'depth_compression_coef'
];

@Component({
    selector: 'app-product-card',
    templateUrl: './product-card.component.html',
    styleUrls: ['./product-card.component.scss'],
	imports: [
		SHARED_MODULES,
		MasterProductsTablesComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		PhLoaderComponent,
		PhButtonComponent,
		ProductChangeHistoryTableComponent,
		ProductCompressionParamsComponent,
		ProductGeneralInfoComponent,
		ProductMasterGeneralInfoComponent,
		ProductPackingEditComponent,
		ProductPhysParamsEditComponent,
		ProductUsageTableComponent
	],
    standalone: true
})
export class ProductCardComponent implements OnInit, OnDestroy {

	activeTab: ProductCardTab = ProductCardTab.Params;
	cardTabs: Array<ProductCardTab> = [];
	defaultProductParams: DefaultProductParams = new DefaultProductParams();
	showLoader: Loader;
	showCommonLoader: boolean = false;
	loadHistoryTab: boolean = false;
	loadMPTab: boolean = false;
	loadUsageTab: boolean = false;
	loadParamsLookup$: ReplaySubject<void> = new ReplaySubject<void>(1);
	paramsEditAccess: boolean = false;
	photoEditAccess: boolean = false;
	product: ProductCard;
	productCardTabs: typeof ProductCardTab = ProductCardTab;
	productParamsFormGroup: FormGroup<IProductCardForm>;
	productTypes: typeof ProductTypes = ProductTypes;
	savingSubscription: Subscription;
	subscriptions: Array<Subscription> = [];
	tinyKeysSubscription: () => void;

	@Input('shopId') shopId: number;

	@ViewChild('formElement') formElement: ElementRef;
	@ViewChild('form') form: NgForm;
	@ViewChild(MasterProductsTablesComponent) masterProductsTablesComponent: MasterProductsTablesComponent;
	@ViewChild(ProductUsageTableComponent) productUsageTableComponent: ProductUsageTableComponent;
	@ViewChild(ProductChangeHistoryTableComponent) productChangeHistoryTableComponent: ProductChangeHistoryTableComponent;

	constructor(
		private dialog: MatDialog,
		private snackBarService: SnackBarService,
		private productCardService: ProductCardService,
		private productsService: ProductsService,
		private sessionService: SessionService,
		private mixPanelService: MixpanelService,
		private dwUsersActivityService: DwUsersActivityService
	) {
		this.paramsEditAccess = this.sessionService.hasAccess('ph-product-parameters-edit');
		this.photoEditAccess = this.sessionService.hasAccess('ph-product-image-edit');
		this.showLoader = { params: true, layout: true, history: true, masterProducts: true };
	}

	private get _packingControl(): FormArray<FormGroup<IProductCardPackingForm>> {
		return this.productParamsFormGroup.controls.packing;
	}

	ngOnInit(): void {
		this.subscriptions.push(
			this.productCardService.saveSubject.subscribe((product: IProductCard) => {
				if (this.product.id === product.id) this.product.isArchived = product.is_archived;
			}),

			this.productCardService.updateSubject.subscribe((data: IShowProductCard) => {
				this.setCardProduct(data);
			}),

			this.productCardService.saveProductsSubject.subscribe(() => {
				if (this.product.loadedLayout && this.productUsageTableComponent) {
					this.productUsageTableComponent.loadData();
				}

				if (this.product.loadedHistory && this.productChangeHistoryTableComponent) {
					this.productChangeHistoryTableComponent.loadData();
				}
			}),

			this.loadParamsLookup$
				.pipe(
					switchMap(() => {
						const params: IProductCardParams = {};
						if (this.shopId) params.shop_id = this.shopId;

						return this.productsService
							.getProductCard(this.product.id, params)
							.pipe(
								catchError((error: HttpErrorResponse) => {
									if (error.status === 404) {
										this.snackBarService.setSnackBarNotification({
											messageText: 'COMMON.PRODUCT.NOT_EXIST'
										});
										this.closeCard(true);
									} else {
										this.snackBarService.setErrorNotification();
									}
									return of([]);
								})
							);
					})
				)
				.subscribe((response: IProductCard) => {
					this.initParams(response);
				})
		);

		this.tinyKeysSubscription = tinykeys(window, {
			'Escape': (event: KeyboardEvent) => {
				event.preventDefault();

				if (!this.dialog.openDialogs.length) this.closeCard();
			},
			'Enter': (event: KeyboardEvent) => {
				if (this.dialog.openDialogs.length) return;
				if (event.target['nodeName'] === 'TEXTAREA') return;
				if (this.activeTab !== ProductCardTab.Params) return;
				event.preventDefault();
				if (
					(
						this.form.submitted &&
						this.productParamsFormGroup.invalid
					) ||
					!this.productParamsFormGroup.dirty ||
					this.productParamsFormGroup.pending
				) return;
				this.form.onSubmit(event);
			}
		});
	}

	private createMasterPosition(params: any): void {
		this.showCommonLoader = true;
		this.productCardService.isDirty.set(ProductCardTab.MasterProducts, false);
		this.productsService.createMasterPosition(params).subscribe({
			next: (response: IProductCard) => {
				this.product = new ProductCard({
					id: response.id,
					type: ProductTypes.MasterPosition,
					isArchived: false,
					name: response.name
				});
				this.setTabs();
				this.initParams(response);
				this.productCardService.openedProduct = response.id;
				this.productCardService.saveSubject.next(response);
				this.snackBarService.setSnackBarNotification({
					messageText: 'MASTER_POSITION.SUCCESSFULLY_CREATED'
				});
				this.mixPanelService.trackPage(MixpanelEvents.MasterPositionCreate);
				this.dwUsersActivityService.track(DwUserActivityNames.Products, DwUserActivityTypes.Create);
			},
			error: () => {
				this.closeCard(true);
				this.snackBarService.setErrorNotification();
			}
		});
	}

	private initParams(response: IProductCard): void {
		this.savingSubscription = null;
		this.product.initParams(response);
		this.setFormGroup();
		this.showLoader.params = false;
		this.showCommonLoader = false;
	}

	private saveProduct(): void {
		this.showLoader.params = true;
		this._packingControl.controls.forEach((packing: FormGroup<IProductCardPackingForm>, index: number) => {
			if (packing.controls.position.value !== index) {
				packing.controls.position.setValue(index);
				packing.controls.position.markAsDirty();
			}
		});
		const dirtyValues = Utils.getDirtyFormValues(this.productParamsFormGroup);
		this.updatingPacking(dirtyValues).subscribe({
			next: () => {
				delete dirtyValues.packing;

				if (dirtyValues.height) dirtyValues.height = UnitConverter.getSaveConvertValue(dirtyValues.height);
				if (dirtyValues.width) dirtyValues.width = UnitConverter.getSaveConvertValue(dirtyValues.width);
				if (dirtyValues.depth) dirtyValues.depth = UnitConverter.getSaveConvertValue(dirtyValues.depth);

				this.savingSubscription = this.productsService.updateProduct(this.product.id, dirtyValues).subscribe({
					next: (product: IProductCard) => {
						this.initParams(product);
						this.productCardService.saveSubject.next(product);
						if (this.product.loadedHistory && this.productChangeHistoryTableComponent) {
							this.productChangeHistoryTableComponent.loadData();
						}
						if (this.product.loadedLayout && this.productUsageTableComponent && dirtyValues.hasOwnProperty('is_loose')) {
							this.productUsageTableComponent.loadData();
						}
					},
					error: () => {
						this.snackBarService.setErrorNotification();
						this.showLoader.params = false;
					}
				});
			},
			error: () => {
				this.snackBarService.setErrorNotification();
				this.showLoader.params = false;
			}
		});
	}

	private scrollToFirstInvalidControl(): void {
		const firstInvalidControl: HTMLElement = this.formElement.nativeElement.getElementsByClassName('ph-input invalid')[0];
		if (!firstInvalidControl) return;
		firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
		firstInvalidControl.focus();
	}

	private setCardProduct(data: IShowProductCard): void {
		if (!data) return;
		if (data.createParams) {
			this.createMasterPosition(data.createParams);
			return;
		}
		this.activeTab = data.tab ?? this.activeTab;
		if (this.product?.id === data.key) return;
		this.showCommonLoader = true;
		this.product = null;
		this.loadMPTab = false;
		this.loadUsageTab = false;
		this.loadHistoryTab = false;
		this.productParamsFormGroup = null;
		this.productCardService.isDirty.set(ProductCardTab.Params, false);

		this.product = new ProductCard({
			id: data.key,
			isArchived: false,
			type: ProductTypes.Product,
			name: data.name
		});

		this.productsService
			.getProductGeneralInfo(data.key)
			.subscribe({
				next: (response: IProductGeneralInfo) => {
					this.product = new ProductCard({
						id: data.key,
						type: response.is_master_position ? ProductTypes.MasterPosition : ProductTypes.Product,
						isArchived: response.is_archived,
						name: data.name
					});
					this.setTabs();
					if (!this.cardTabs.includes(this.activeTab)) this.activeTab = ProductCardTab.Params;
					this.loadData();
				},
				error: () => {
					this.closeCard(true);
					this.snackBarService.setErrorNotification();
				}
			});
	}

	private setTabs(): void {
		if (!this.product) return;
		switch (this.product.type) {
			case ProductTypes.Product: {
				this.cardTabs = [ProductCardTab.Params, ProductCardTab.Layout, ProductCardTab.History];
				break;
			}
			case ProductTypes.MasterPosition: {
				this.cardTabs = [ProductCardTab.Params, ProductCardTab.MasterProducts, ProductCardTab.Layout, ProductCardTab.History];
				this.productCardService.isDirty.set(ProductCardTab.MasterProducts, false);
				break;
			}
			default: return;
		}
	}

	private setFormGroup(): void {
		this.productCardService.isDirty.set(ProductCardTab.Params, false);
		this.productParamsFormGroup = new FormGroup<IProductCardForm>({
			name: new FormControl<string>(
				{ value: this.product.params.name, disabled: true },
				[Validators.required, Validators.maxLength(200)]
			),
			article: new FormControl<string>(
				{ value: this.product.params.article, disabled: true },
				[Validators.required, Validators.maxLength(100)]
			),
			description: new FormControl<string>(
				{ value: this.product.params.description, disabled: true },
				[Validators.maxLength(500)]
			),
			is_loose: new FormControl<boolean>(!!this.product.params.is_loose, [Validators.required]),
			photo: new FormControl<string>(this.product.params.photo),
			height: new FormControl<number>(UnitConverter.getConvertValue(this.product.params.height || this.defaultProductParams.height), [
				Validators.required,
				Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
				Validators.pattern('(\\d+\\.?)+')
			]),
			width: new FormControl<number>(UnitConverter.getConvertValue(this.product.params.width || this.defaultProductParams.width), [
				Validators.required,
				Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
				Validators.pattern('(\\d+\\.?)+')
			]),
			depth: new FormControl<number>(UnitConverter.getConvertValue(this.product.params.depth || this.defaultProductParams.depth), [
				Validators.required,
				Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
				Validators.pattern('(\\d+\\.?)+')
			]),
			width_compression_coef: new FormControl<number>(
				this.product.params.width_compression_coef, [
					ProductsService.coefficientValidator(),
					Validators.pattern('(\\d+\\.?)+'),
					Validators.required
				]
			),
			height_compression_coef: new FormControl<number>(
				this.product.params.height_compression_coef, [
					ProductsService.coefficientValidator(),
					Validators.pattern('(\\d+\\.?)+'),
					Validators.required
				]
			),
			depth_compression_coef: new FormControl<number>(
				this.product.params.depth_compression_coef, [
					ProductsService.coefficientValidator(),
					Validators.pattern('(\\d+\\.?)+'),
					Validators.required
				]
			),
			set_compression_to_global_everywhere: new FormControl<boolean>(false),
			packing: new FormArray<FormGroup<IProductCardPackingForm>>(
				this.product.params.packing.map((packing: IProductPacking) => new FormGroup<IProductCardPackingForm>({
					id: new FormControl<number>(packing.id),
					name: new FormControl<string>(packing.name, [
						Validators.maxLength(100),
						Validators.required,
						RxwebValidators.unique()
					]),
					qty: new FormControl<number>(packing.qty, [
						Validators.required,
						Validators.min(1),
						Validators.pattern('\\d+')
					]),
					weight: new FormControl<number>(UnitConverter.getConvertValue(packing.weight, 'mass'), [
						Validators.required,
						Validators.min(UnitConverter.getConvertValue(0.001, 'mass')),
						Validators.pattern('(\\d+\\.?)+')
					]),
					height: new FormControl<number>(UnitConverter.getConvertValue(packing.height), [
						Validators.required,
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+')
					]),
					depth: new FormControl<number>(UnitConverter.getConvertValue(packing.depth), [
						Validators.required,
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+')
					]),
					width: new FormControl<number>(UnitConverter.getConvertValue(packing.width), [
						Validators.required,
						Validators.min(GlobalSettings.MIN_ELEMENT_SIZES.get(this.sessionService.defaults.measurement_system)),
						Validators.pattern('(\\d+\\.?)+')
					]),
					photo: new FormControl<string>(packing.photo),
					color: new FormControl<string>(Utils.getRandomColor()),
					position: new FormControl<number>(packing.position)
				}))
			)
		});

		this._packingControl.valueChanges.subscribe(() => {
			this._packingControl.controls.forEach((control: FormGroup<IProductCardPackingForm>) => {
				control.controls.name.updateValueAndValidity({ emitEvent: false });
			});
		});

		this.productParamsFormGroup.valueChanges.subscribe(() => {
			this.productCardService.isDirty.set(ProductCardTab.Params, this.productParamsFormGroup.dirty);
		});

		this.productParamsFormGroup.controls.is_loose.valueChanges.subscribe(() => this.updateFieldsState());
		this.updateFieldsState();
	}

	private updateFieldsState(): void {
		const value: boolean = this.productParamsFormGroup.controls.is_loose.value;

		INPUT_CONTROL_NAME.forEach((name: string) => value ?
			this.productParamsFormGroup.get(name).disable() :
			this.productParamsFormGroup.get(name).enable()
		);
		if (value) {
			this._packingControl.controls.forEach((control: FormGroup<IProductCardPackingForm>) => control.controls.qty.disable());
			this._packingControl.controls.forEach((control: FormGroup<IProductCardPackingForm>) => control.controls.weight.enable());
		} else {
			this._packingControl.controls.forEach((control: FormGroup<IProductCardPackingForm>) => control.controls.weight.disable());
			this._packingControl.controls.forEach((control: FormGroup<IProductCardPackingForm>) => control.controls.qty.enable());
		}
	}

	private updatingPacking(dirtyValues: any): Observable<any> {
		const creating: IProductPacking[] = [];
		this._packingControl.value.forEach((packing: IProductPacking) => {
			if (!packing.id) {
				delete packing.id;
				creating.push(packing);
			}
		});

		const requests: Array<Observable<any>> = [];
		const oldPackingIds: Array<number> = this.product.params.packing.map((packing: IProductPacking) => packing.id);
		const newPackingIds: Array<number> = this._packingControl.value.map((packing) => packing.id);
		const deletedPackingIds: Array<number> = oldPackingIds.filter((id: number) => !newPackingIds.includes(id));
		if (deletedPackingIds.length)
			requests.push(
				...deletedPackingIds.map(packing => this.productsService.deletePacking(this.product.id, packing))
			);
		if (dirtyValues.packing) {
			dirtyValues.packing = dirtyValues.packing.filter((packing: IProductPacking) => packing.id);
			if (dirtyValues.packing.length)
				requests.push(
					...dirtyValues.packing.map(packing => {
						if (packing.height) packing.height = UnitConverter.getSaveConvertValue(packing.height);
						if (packing.width) packing.width = UnitConverter.getSaveConvertValue(packing.width);
						if (packing.depth) packing.depth = UnitConverter.getSaveConvertValue(packing.depth);
						if (packing.weight) packing.weight = UnitConverter.getSaveConvertValue(packing.weight, 'mass');

						return this.productsService.updatePacking(this.product.id, packing);
					})
				);
		}
		if (creating.length)
			requests.push(
				...creating.map(packing => {
					if (packing.height) packing.height = UnitConverter.getSaveConvertValue(packing.height);
					if (packing.width) packing.width = UnitConverter.getSaveConvertValue(packing.width);
					if (packing.depth) packing.depth = UnitConverter.getSaveConvertValue(packing.depth);
					if (packing.weight) packing.weight = UnitConverter.getSaveConvertValue(packing.weight, 'mass');

					return this.productsService.createPacking(this.product.id, packing);
				})
			);

		return requests.length ?
			forkJoin(
				from(requests).pipe(
					concatMap(request => request)
				)
			) :
			of([]);
	}

	closeCard(force?: boolean): void {
		this.productCardService.close({ force });
	}

	loadData(): void {
		switch (this.activeTab) {
			case ProductCardTab.Params: {
				if (!this.product.loadedParams) this.loadProductParams();
				break;
			}
			case ProductCardTab.Layout: {
				this.loadUsageTab = true;
				this.showCommonLoader = false;
				break;
			}
			case ProductCardTab.History: {
				this.loadHistoryTab = true;
				this.showCommonLoader = false;
				break;
			}
			case ProductCardTab.MasterProducts: {
				this.loadMPTab = true;
				this.showCommonLoader = false;
				break;
			}
			default: return;
		}
	}

	loadProductParams(): void {
		this.showLoader.params = true;
		this.loadParamsLookup$.next();
	}

	onCancel(tab: ProductCardTab): void {
		const dirtyEntries: Array<[ProductCardTab, boolean]> = Array.from(this.productCardService.isDirty.entries());
		const foundEntry: [ProductCardTab, boolean] = dirtyEntries.find(([key, value]) => key !== tab && value);
		const foundTab: ProductCardTab = foundEntry?.[0];
		if (foundTab) this.activeTab = foundTab;
		this.closeCard(!foundTab);
	}

	onRevertChange(type: ProductChangeTypes): void {
		if (this.product.loadedParams && type !== ProductChangeTypes.Products) {
			this.loadProductParams();
		}

		if (this.product.loadedMasterProducts && type === ProductChangeTypes.Products) {
			this.masterProductsTablesComponent.loadData();
			this.masterProductsTablesComponent.needUpdateAddProducts = true;
		}
	}

	onSubmit(): void {
		['width_compression_coef', 'height_compression_coef', 'depth_compression_coef'].forEach((key: string) => {
			this.productParamsFormGroup
				.get(key)
				.setValue(Utils.roundToFixed(this.productParamsFormGroup.get(key).value, 2) || 0);
		});
		['height', 'width', 'depth', 'weight'].forEach((key: string) => {
			const value = this.productParamsFormGroup.get(key)?.value;
			if (value) {
				this.productParamsFormGroup
					.get(key)
					.setValue(Utils.roundToFixed(this.productParamsFormGroup.get(key).value));
			}
			this._packingControl.controls.forEach(packing => {
				const packingValue = packing.get(key)?.value;
				if (packingValue) {
					packing
						.get(key)
						.setValue(Utils.roundToFixed(packing.get(key).value));
				}
			});
		});

		if (this.productParamsFormGroup.invalid) {
			setTimeout(() => {
				this.scrollToFirstInvalidControl();
			})
			return;
		}

		if (!this.sessionService.defaults.settings.hideProductWarning) {
			this.dialog
				.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '700px',
					height: 'auto',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'CHANGE_PRODUCT_INFO',
						twoAnswers: true,
						confirmLabel: 'COMMON.CONFIRM',
						cancelLabel: 'COMMON.CANCEL',
						checkBox: true,
						checkBoxLabel: 'NOT_SHOW_ANYMORE'
					}
				})
				.afterClosed()
				.subscribe((result: IConfirmModalAnswer) => {
					if (!result.answer) return;

					if (result.checkBoxState) {
						this.sessionService.defaults.settings.hideProductWarning = result.answer;
						this.sessionService.updateSettings();
					}

					this.saveProduct();
				});
		} else {
			this.saveProduct();
		}
	}

	packingDrop(event: CdkDragDrop<Array<FormGroup<IProductCardPackingForm>>>): void {
		moveItemInArray(this._packingControl.controls, event.previousIndex, event.currentIndex);
		this._packingControl.markAsDirty();
		this.productCardService.isDirty.set(ProductCardTab.Params, true);
	}

	tabIndexChange(index: number): void {
		this.activeTab = this.cardTabs[index];
		if (this.activeTab !== ProductCardTab.MasterProducts && this.masterProductsTablesComponent) {
			this.masterProductsTablesComponent.onCloseSelectedTable();
		}
		this.loadData();
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
		if (this.tinyKeysSubscription) this.tinyKeysSubscription();
		if (this.savingSubscription) this.savingSubscription.unsubscribe();
		this.productCardService.isDirty.delete(ProductCardTab.Params);
		this.productCardService.isDirty.delete(ProductCardTab.MasterProducts);
	}
}
